import { useSubscription } from '../../graphql';
import { useCurrentAccount } from '../../auth/hooks/useCurrentAccount';

export interface IncomingCall {
  id: string;
  room: string;
  state: string;
  caller: {
    id: string;
    name?: { first?: string; last?: string };
  };
  callee: { id: string };
  /** Set for activity (group) calls; absent for 1:1 calls. */
  issue?: { id: string } | null;
}

/**
 * Rings the current account when someone calls them. Mirrors
 * useUnreadActivityInboxSubscription — subscribes to `newCall` scoped to calls
 * where the current account is the callee. Mount once high in the app tree.
 *
 * NOTE: this WebSocket subscription only rings a *foregrounded* app — a
 * backgrounded/killed mobile device has no live socket, so it won't fire.
 * Native wake is a separate path: `notifyIncomingCall` (functions/) pushes
 * APNs VoIP / high-priority FCM on `call.created`, and gripp-app reports
 * that to CallKit / Core-Telecom via `expo-callkit-telecom`. See
 * spec/technical-plans/0018-livekit-in-app-calling.md Phase 1.5.
 */
export const useIncomingCall = (
  onIncomingCall: (call: IncomingCall) => Promise<void>,
  /**
   * Gate for the incoming-call subscription. Defaults to true; callers pass the
   * COMMUNICATION_PLATFORM feature flag so a flag-off user never even receives a
   * ring (initiation is already flag-gated at each entry point). Combined with
   * `account != null` below.
   */
  enabled = true
) => {
  const { account } = useCurrentAccount();

  useSubscription(
    {
      query: INCOMING_CALL_SUBSCRIPTION,
      variables: {
        filter: {
          and: [
            { callee: { eq: account?.id } },
            { state: { eq: 'RINGING' } },
          ],
        },
      },
    },
    async (response) => {
      await onIncomingCall(response.data.newCall);
    },
    account != null && enabled
  );
};

const INCOMING_CALL_SUBSCRIPTION = `
subscription newCall($filter: CallSubscriptionFilter) {
  newCall(filter: $filter) {
    id
    room
    state
    caller {
      id
      name {
        first
        last
      }
    }
    callee {
      id
    }
    issue {
      id
    }
  }
}
`;
